import api from './client';

// Enums
export enum TransactionType {
  SALE = 'SALE',
  PURCHASE = 'PURCHASE',
  EXPENSE = 'EXPENSE',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

export enum ItemType {
  BIRD = 'BIRD',
  SUPPLY = 'SUPPLY',
  EQUIPMENT = 'EQUIPMENT',
  SERVICE = 'SERVICE',
  FEED = 'FEED',
  OTHER = 'OTHER',
}

export enum PaymentMethod {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  OTHER = 'OTHER',
}

// Interfaces for integrated transactions
export interface TransactionItem {
  itemType: ItemType;
  itemReferenceId?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  notes?: string;
}

export interface BirdSaleTransactionRequest {
  birdId: string;
  buyerName: string;
  buyerPhone: string;
  buyerAddress: string;
  salePrice: number;
  paymentMethod: PaymentMethod;
  saleDate: Date;
  notes?: string;
}

export interface BirdPurchaseTransactionRequest {
  birdId: string;
  supplierName: string;
  supplierPhone: string;
  supplierAddress: string;
  purchasePrice: number;
  paymentMethod: PaymentMethod;
  purchaseDate: Date;
  notes?: string;
}

export interface HealthTestTransactionRequest {
  healthTestId: string;
  cost: number;
  paymentMethod: PaymentMethod;
  accountId?: string;
}

export interface InventoryPurchaseTransactionRequest {
  items: Array<{
    type: 'FEED' | 'SUPPLY' | 'EQUIPMENT';
    itemId: string;
    quantity: number;
    unitPrice: number;
  }>;
  supplierId?: string;
  supplierName: string;
  supplierPhone: string;
  supplierAddress: string;
  paymentMethod: PaymentMethod;
  purchaseDate: Date;
  notes?: string;
}

export interface TransactionResult {
  id: string;
  type: TransactionType;
  status: TransactionStatus;
  invoiceNumber: string;
  transactionDate: string;
  total: number;
  paymentMethod: string;
  counterpartyName: string;
  counterpartyPhone?: string;
  counterpartyAddress?: string;
  notes?: string;
  items: Array<{
    id: string;
    itemType: ItemType;
    itemReferenceId?: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    notes?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  // Legacy fields for compatibility
  totalAmount?: number;
  accountingEntries?: Array<{
    accountId: string;
    accountName: string;
    debitAmount: number;
    creditAmount: number;
  }>;
}

export interface TransactionSummary {
  totalTransactions: number;
  totalAmount: number;
  totalSales: number;
  totalSalesAmount: number;
  totalPurchases: number;
  totalPurchasesAmount: number;
  totalExpenses: number;
  totalExpensesAmount: number;
  pendingTransactions: number;
  pendingSales: number;
  pendingPurchases: number;
  pendingExpenses: number;
  transactionsByType: Record<TransactionType, number>;
  transactionsByStatus: Record<TransactionStatus, number>;
  recentTransactions?: TransactionResult[];
}

// API functions for integrated transactions
export const transactionsApi = {
  // Bird Sale Transaction - Creates sale, expense entries for health tests, and accounting entries
  async createBirdSaleTransaction(data: BirdSaleTransactionRequest): Promise<any> {
    const response = await api.post<any>('transactions/bird-sale', data);
    return response.data;
  },

  // Bird Purchase Transaction - Creates purchase, birds, and accounting entries
  async createBirdPurchaseTransaction(data: BirdPurchaseTransactionRequest): Promise<any> {
    const response = await api.post<any>('transactions/bird-purchase', data);
    return response.data;
  },

  // Health Test Transaction - Creates health test, expense, and accounting entries
  async createHealthTestTransaction(data: HealthTestTransactionRequest): Promise<any> {
    const response = await api.post<any>('transactions/health-test-expense', data);
    return response.data;
  },

  // Inventory Purchase Transaction - Creates purchase, updates inventory, and accounting entries
  async createInventoryPurchaseTransaction(data: InventoryPurchaseTransactionRequest): Promise<any> {
    const response = await api.post<any>('transactions/inventory-purchase', data);
    return response.data;
  },

  // Get all transactions
  async getTransactions(filters: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{
    transactions: TransactionResult[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get<{
      transactions: TransactionResult[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(`transactions?${params.toString()}`);
    
    return response.data;
  },

  // Get transaction summary
  async getTransactionSummary(filters: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<TransactionSummary> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get<TransactionSummary>(`transactions/summary?${params.toString()}`);
    return response.data;
  },

  // Process bird sale transaction
  async processBirdSale(transaction: BirdSaleTransactionRequest): Promise<TransactionResult> {
    const response = await api.post<TransactionResult>('transactions/bird-sale', transaction);
    return response.data;
  },

  // Process bird purchase transaction
  async processBirdPurchase(transaction: BirdPurchaseTransactionRequest): Promise<TransactionResult> {
    const response = await api.post<TransactionResult>('transactions/bird-purchase', transaction);
    return response.data;
  },

  // Process inventory purchase transaction
  async processInventoryPurchase(transaction: InventoryPurchaseTransactionRequest): Promise<TransactionResult> {
    const response = await api.post<TransactionResult>('transactions/inventory-purchase', transaction);
    return response.data;
  },

  // Process supplier payment
  async processSupplierPayment(paymentData: {
    supplierId: string;
    amount: number;
    paymentMethod: PaymentMethod;
    paymentDate: Date;
    description?: string;
    notes?: string;
    reference?: string;
  }): Promise<any> {
    const response = await api.post<any>('transactions/supplier-payment', {
      ...paymentData,
      paymentDate: paymentData.paymentDate.toISOString().split('T')[0]
    });
    return response.data;
  },

  // Get transaction by ID
  async getTransaction(id: string): Promise<TransactionResult> {
    const response = await api.get<TransactionResult>(`transactions/${id}`);
    return response.data;
  },

  // Approve a pending transaction
  async approveTransaction(id: string): Promise<TransactionResult> {
    const response = await api.patch<TransactionResult>(`transactions/${id}/approve`);
    return response.data;
  },

  // Update transaction
  async updateTransaction(id: string, updateData: any): Promise<TransactionResult> {
    const response = await api.put<TransactionResult>(`transactions/${id}`, updateData);
    return response.data;
  },

  // Delete transaction
  async deleteTransaction(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`transactions/${id}`);
    return response.data;
  }
};

// Validation Functions

export const validateTransaction = (transaction: TransactionResult): string[] => {
  const errors: string[] = [];
  
  if (!transaction.type) {
    errors.push('Transaction type is required');
  }
  
  if (!transaction.total || transaction.total <= 0) {
    errors.push('Transaction total must be greater than 0');
  }
  
  if (!transaction.transactionDate) {
    errors.push('Transaction date is required');
  }
  
  return errors;
};

export const validateBirdSaleTransaction = (data: BirdSaleTransactionRequest): string[] => {
  const errors: string[] = [];
  
  if (!data.birdId?.trim()) {
    errors.push('Bird ID is required');
  }
  
  if (!data.buyerName?.trim()) {
    errors.push('Buyer name is required');
  }
  
  if (!data.salePrice || data.salePrice <= 0) {
    errors.push('Sale price must be greater than 0');
  }
  
  if (!data.saleDate) {
    errors.push('Sale date is required');
  }
  
  return errors;
};

export const validateBirdPurchaseTransaction = (data: BirdPurchaseTransactionRequest): string[] => {
  const errors: string[] = [];
  
  if (!data.birdId?.trim()) {
    errors.push('Bird ID is required');
  }
  
  if (!data.supplierName?.trim()) {
    errors.push('Supplier name is required');
  }
  
  if (!data.purchasePrice || data.purchasePrice <= 0) {
    errors.push('Purchase price must be greater than 0');
  }
  
  if (!data.purchaseDate) {
    errors.push('Purchase date is required');
  }
  
  return errors;
};

export const validateInventoryPurchaseTransaction = (data: InventoryPurchaseTransactionRequest): string[] => {
  const errors: string[] = [];
  
  if (!data.supplierName?.trim()) {
    errors.push('Supplier name is required');
  }
  
  if (!data.purchaseDate) {
    errors.push('Purchase date is required');
  }
  
  if (!data.items || data.items.length === 0) {
    errors.push('At least one item must be added');
  }
  
  data.items?.forEach((item, index) => {
    if (!item.itemId?.trim()) {
      errors.push(`Item ${index + 1}: Item ID is required`);
    }
    if (!item.type) {
      errors.push(`Item ${index + 1}: Item type is required`);
    }
    if (!item.quantity || item.quantity <= 0) {
      errors.push(`Item ${index + 1}: Valid quantity is required`);
    }
    if (!item.unitPrice || item.unitPrice <= 0) {
      errors.push(`Item ${index + 1}: Valid unit price is required`);
    }
  });
  
  return errors;
};

// Helper functions
export const getTransactionTypeDisplay = (type: TransactionType): string => {
  switch (type) {
    case TransactionType.SALE:
      return 'Sale';
    case TransactionType.PURCHASE:
      return 'Purchase';
    case TransactionType.EXPENSE:
      return 'Expense';
    default:
      return type;
  }
};

export const getTransactionStatusColor = (status: TransactionStatus): string => {
  switch (status) {
    case TransactionStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case TransactionStatus.COMPLETED:
      return 'bg-green-100 text-green-800 border-green-200';
    case TransactionStatus.CANCELLED:
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case TransactionStatus.FAILED:
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getItemTypeDisplay = (itemType: ItemType): string => {
  switch (itemType) {
    case ItemType.BIRD:
      return 'Bird';
    case ItemType.SUPPLY:
      return 'Supply';
    case ItemType.EQUIPMENT:
      return 'Equipment';
    case ItemType.SERVICE:
      return 'Service';
    case ItemType.FEED:
      return 'Feed';
    case ItemType.OTHER:
      return 'Other';
    default:
      return itemType;
  }
};

export const formatCurrency = (amount: number, currency = 'ZAR'): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const calculateTransactionTotal = (items: TransactionItem[]): number => {
  return items.reduce((total, item) => total + item.totalPrice, 0);
};

